import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  RadioGroup,
  Radio,
  ToggleButton,
  ToggleButtonGroup,
  Pagination,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore,
  Check,
  Close,
  Visibility,
  ChevronRight,
  CheckCircle,
  CheckCircleOutline,
  InfoOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Navigation from '../Shared/Navigation.tsx';
import LightTooltip from '../Shared/LightTooltip';
import { shouldShowSelfAssessmentResultsStep } from './resultsStepAvailability';
import SignatureCanvasLib from 'react-signature-canvas';
const SigCanvas: any = SignatureCanvasLib;
import { useTranslation } from '@/core/i18n/i18n';
import {
  Activity,
  ActivityTypes,
  Focus,
  FocusRequirementWithDetails,
  Objective,
} from '@shared/schema';
import { ChecklistWithRelations, ChecklistSavedResponse } from '@/core/types/state';
import PlanAnualIcon from '@/static/icons/Plan-anual.svg?url';
import CalendarIcon from '@/static/icons/calendar.svg?url';
import { useAnyLoading } from '@/core/useAnyLoading';

interface ExtendedFocus extends Focus {
  FulfillmentProgressPct?: number;
}

interface ProcessedChecklist {
  ChecklistId: number;
  Name: string;
  progress: number;
  CategoryId?: number | null;
  CategoryName?: string | null;
}

interface CategoryData {
  categoryName: string;
  checklists: ProcessedChecklist[];
  minScore: number;
  maxScore: number;
  avgScore: number;
}

interface PlanValidationResponse {
  Approved?: boolean;
  CEOConfirmation?: boolean;
  Comment?: string;
  Signature?: string;
  SignatureName?: string;
  APValidationId?: number;
}

interface CEOValidationProps {
  objectives: Objective[];
  requirements: FocusRequirementWithDetails[];
  activities: Activity[];
  focuses: ExtendedFocus[];
  currentVersion: number | null;
  currentPlanId: number | null;
  currentFulfillmentVersion: number | null;
  checklists: ChecklistWithRelations[];
  checklistResponses: ChecklistSavedResponse[];
  fetchObjectivesByVersionId: (versionId: number) => void;
  getActivitiesByObjectiveId: (objectiveId: number, fulfillmentId: number) => void;
  getRequirements: (params?: { planId?: number; year?: number }) => void;
  selectedYear: number | null;
  fetchAllFocuses: (versionId: number) => void;
  readOnlyMode: boolean | null;
  fetchFocusesFulfillmentProgress?: (fulfillmentVersionId: number) => void;
  fetchChecklistsByVersionId: (APFulfillmentsVersionId: number) => void;
  fetchChecklistResponsesByVersionId: (APFulfillmentsVersionId: number) => void;
  addFocusFulfillmentValidation: (
    fulfillmentVersionId: number,
    data: { APFocusId: number; Approved: boolean },
  ) => Promise<void>;
  addPlanValidation: (
    fulfillmentVersionId: number,
    data: { Approved: boolean; CEOConfirmation: boolean; Comment: string; Signature: string },
  ) => Promise<void>;
  uploadPlanSignature: (
    fulfillmentVersionId: number,
    dataUrl: string,
  ) => Promise<{ name: string; url: string }>;
  getPlanValidation: (fulfillmentVersionId: number) => Promise<PlanValidationResponse | undefined>;
  planValidation?: PlanValidationResponse;
  getFocusFulfillmentValidationsThunk: (
    fulfillmentVersionId: number,
  ) => Promise<{ APFocusFulfillmentId: number; APFocusId: number; Approved: boolean }[]>;
  scopeName: string;
  currentBusinessUnit?: string | null;
  focusFulfillmentValidations: FocusFulfillmentValidation[];
}

interface FocusFulfillmentValidation {
  APFocusFulfillmentId: number;
  APFocusId: number;
  Approved: boolean;
}

const formatPercentage = (value: number | null | undefined, digits = 1) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  const rounded = Number(num.toFixed(digits));
  const safe = rounded === 0 ? 0 : rounded;
  const str = safe.toFixed(digits);
  return str.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
};

const CEOValidation: React.FC<CEOValidationProps> = ({
  objectives,
  requirements,
  activities,
  focuses,
  fetchObjectivesByVersionId,
  getActivitiesByObjectiveId,
  getRequirements,
  fetchAllFocuses: _fetchAllFocuses,
  selectedYear,
  fetchFocusesFulfillmentProgress,
  currentVersion,
  currentPlanId,
  readOnlyMode,
  currentFulfillmentVersion,
  checklists,
  checklistResponses,
  fetchChecklistsByVersionId,
  fetchChecklistResponsesByVersionId,
  getPlanValidation,
  planValidation,
  addFocusFulfillmentValidation,
  addPlanValidation,
  getFocusFulfillmentValidationsThunk,
  uploadPlanSignature,
  scopeName,
  currentBusinessUnit,
  focusFulfillmentValidations,
}) => {
  const isAnyLoading = useAnyLoading();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [expandedAccordion, setExpandedAccordion] = useState(true);
  const [validationChecked, setValidationChecked] = useState(false);
  const [comments, setComments] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [selectedFocus, setSelectedFocus] = useState<Focus | null>(null);
  const [selectedObjective, setSelectedObjective] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [decisionStatus, setDecisionStatus] = useState<'approve' | 'reject' | null>(null);
  const [signatureName, setSignatureName] = useState<string | null>(null);
  interface SignaturePadHandle {
    isEmpty: () => boolean;
    clear: () => void;
    toDataURL: () => string;
    getTrimmedCanvas: () => HTMLCanvasElement;
  }
  const signatureRef = useRef<SignaturePadHandle | null>(null);
  const [decisionByFocus, setDecisionByFocus] = useState<Record<number, 'approve' | 'reject'>>({});
  const [loadedSignature, setLoadedSignature] = useState<string | null>(null);
  const [showSignatureCanvas, setShowSignatureCanvas] = useState(true);
  const [_existingValidationId, setExistingValidationId] = useState<number | null>(null);
  interface ExtendedPlanValidationInput {
    Approved: boolean;
    CEOConfirmation: boolean;
    Comment: string;
    Signature: string;
    IsDraft?: boolean;
  }
  const submitPlanValidation = (fvId: number, payload: ExtendedPlanValidationInput) =>
    addPlanValidation(
      fvId,
      payload as unknown as {
        Approved: boolean;
        CEOConfirmation: boolean;
        Comment: string;
        Signature: string;
        IsDraft?: boolean;
      },
    );

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [expandedChecklistCategories, setExpandedChecklistCategories] = useState<
    Record<string, boolean>
  >({});

  const [activitiesByObjective, setActivitiesByObjective] = useState<Record<number, Activity[]>>(
    {},
  );

  const handleUploadSignature = async () => {
    if (!currentFulfillmentVersion) {
      setAlertMessage(t('ceoValidation.noFulfillmentVersion'));
      return;
    }
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      try {
        const dataUrl = signatureRef.current.toDataURL();
        const image = await uploadPlanSignature(currentFulfillmentVersion, dataUrl);
        setLoadedSignature(image.url);
        setSignatureName(image.name);
        setShowSignatureCanvas(false);
        setAlertMessage(t('ceoValidation.signatureUploaded'));
      } catch (e) {
        console.error(e);
        setAlertMessage(t('ceoValidation.signatureUploadError'));
      }
    } else {
      setAlertMessage(t('ceoValidation.noSignatureToUpload'));
    }
  };

  const isValidationComplete = React.useMemo(() => {
    if (!decisionStatus) return false;
    const allFocusDecisionsComplete = focuses.every(
      (focus) => decisionByFocus[focus.APFocusId] !== undefined,
    );
    if (!allFocusDecisionsComplete) return false;
    if (decisionStatus === 'approve' && !validationChecked) return false;
    if (!signatureName) return false;
    if (!comments.trim()) return false;
    return true;
  }, [decisionStatus, decisionByFocus, focuses, validationChecked, comments, signatureName]);

  const disableNext = readOnlyMode ? false : !isValidationComplete;
  const disableNextReason = React.useMemo(() => {
    if (readOnlyMode) return undefined;
    if (!decisionStatus) {
      return t('ceoValidation.disableReasonSelectDecision');
    }
    const incompleteFocusDecision = focuses.some(
      (focus) => decisionByFocus[focus.APFocusId] === undefined,
    );
    if (incompleteFocusDecision) {
      return t('ceoValidation.disableReasonCompleteFocusDecisions');
    }
    if (decisionStatus === 'approve' && !validationChecked) {
      return t('ceoValidation.mustConfirmValidation');
    }
    if (!signatureName) {
      return t('ceoValidation.mustProvideSignature');
    }
    if (!comments.trim()) {
      return t('ceoValidation.disableReasonCommentsRequired');
    }
    return undefined;
  }, [
    readOnlyMode,
    decisionStatus,
    focuses,
    decisionByFocus,
    validationChecked,
    signatureName,
    comments,
    t,
  ]);

  const showResultsStep = React.useMemo(
    () =>
      shouldShowSelfAssessmentResultsStep(readOnlyMode, planValidation?.Approved, selectedYear),
    [planValidation?.Approved, readOnlyMode, selectedYear],
  );
  const handleGoToResults = React.useCallback(() => {
    if (!showResultsStep) return;
    navigate('/self-assessment-results');
  }, [navigate, showResultsStep]);

  const isStep3Completed = showResultsStep;
  const stepGridSize = showResultsStep ? { xs: 12, sm: 6, md: 3 } : { xs: 12, sm: 6, md: 4 };

  const globalAdherence = React.useMemo(() => {
    if (!focuses || focuses.length === 0) return 0;
    let totalWeighted = 0;
    let totalWeight = 0;
    focuses.forEach((f) => {
      const pct = f.FulfillmentProgressPct ?? 0;
      const weight = f.Weight || 0;
      totalWeighted += pct * weight;
      totalWeight += weight;
    });
    return totalWeight > 0 ? totalWeighted / totalWeight : 0;
  }, [focuses]);

  useEffect(() => {
    const versionId = currentVersion;
    const parsedYear = Number.isInteger(selectedYear) ? selectedYear : null;
    getRequirements({ planId: currentPlanId ?? undefined, year: parsedYear ?? undefined });
    if (versionId) {
      fetchObjectivesByVersionId(versionId);
    }
    if (currentFulfillmentVersion && fetchFocusesFulfillmentProgress) {
      fetchFocusesFulfillmentProgress(currentFulfillmentVersion);
      fetchChecklistsByVersionId(currentFulfillmentVersion);
    }
    if (currentFulfillmentVersion) {
      getPlanValidation(currentFulfillmentVersion);
      getFocusFulfillmentValidationsThunk(currentFulfillmentVersion);
    }
  }, [
    currentVersion,
    currentPlanId,
    selectedYear,
    getRequirements,
    fetchObjectivesByVersionId,
    currentFulfillmentVersion,
    fetchFocusesFulfillmentProgress,
    fetchChecklistsByVersionId,
    getPlanValidation,
    getFocusFulfillmentValidationsThunk,
  ]);

  useEffect(() => {
    if (focusFulfillmentValidations && focusFulfillmentValidations.length > 0) {
      setDecisionByFocus((prev) => {
        const newDecisions = { ...prev };
        focusFulfillmentValidations.forEach((validation) => {
          newDecisions[validation.APFocusId] = validation.Approved ? 'approve' : 'reject';
        });
        return newDecisions;
      });
    }
  }, [focusFulfillmentValidations]);

  useEffect(() => {
    if (planValidation) {
      if (typeof planValidation.Approved === 'boolean') {
        setDecisionStatus(planValidation.Approved ? 'approve' : 'reject');
      }
      if (typeof planValidation.CEOConfirmation === 'boolean') {
        setValidationChecked(planValidation.CEOConfirmation);
      }
      if (typeof planValidation.Comment === 'string') {
        setComments(planValidation.Comment);
      }
      if (planValidation.Signature) {
        setLoadedSignature(planValidation.Signature);
        setSignatureName(planValidation.SignatureName ?? '');
        setShowSignatureCanvas(false);
      }
      if (planValidation.APValidationId) {
        setExistingValidationId(planValidation.APValidationId);
      }
    }
  }, [planValidation]);

  useEffect(() => {
    if (checklists.length > 0 && currentFulfillmentVersion) {
      fetchChecklistResponsesByVersionId(currentFulfillmentVersion);
    }
  }, [checklists, currentFulfillmentVersion, fetchChecklistResponsesByVersionId]);

  const objectivesByFocus = React.useMemo(() => {
    const grouped: Record<number, Objective[]> = {};
    objectives?.forEach((o) => {
      if (!grouped[o.APFocusId]) grouped[o.APFocusId] = [];
      grouped[o.APFocusId]!.push(o);
    });
    return grouped;
  }, [objectives]);

  useEffect(() => {
    if (activities && activities.length > 0) {
      const first = activities[0];
      if (!first) return;
      const objectiveId: number = first.APObjectiveId;
      setActivitiesByObjective((prev) => {
        const existing = prev[objectiveId] || [];
        const ids = new Set(existing.map((a: Activity) => a.APGroupActivityId));
        const merged: Activity[] = [...existing];
        activities.forEach((a: Activity) => {
          if (!ids.has(a.APGroupActivityId)) merged.push(a);
        });
        return { ...prev, [objectiveId]: merged };
      });
    }
  }, [activities]);

  useEffect(() => {
    setDecisionByFocus((prev) => {
      const initialDecisions: Record<number, 'approve' | 'reject'> = { ...prev };
      focuses.forEach((f) => {
        if (!(f.APFocusId in initialDecisions)) {
          initialDecisions[f.APFocusId] = 'reject';
        }
      });
      return initialDecisions;
    });
  }, [focuses]);

  const allFocusApproved = React.useMemo(
    () => focuses.length > 0 && focuses.every((f) => decisionByFocus[f.APFocusId] === 'approve'),
    [focuses, decisionByFocus],
  );

  useEffect(() => {
    if (!allFocusApproved && !decisionStatus) {
      setDecisionStatus('reject');
    }
  }, [allFocusApproved, decisionStatus]);

  const processedChecklistData = React.useMemo(() => {
    if (!checklists || checklists.length === 0) return {};

    const allChecklists = checklists.flatMap((scope) => scope.Checklists || []);

    const categorizedChecklists = allChecklists.reduce(
      (acc, checklist) => {
        const categoryKey = (checklist.CategoryId ?? 'null').toString();
        const categoryName = checklist.CategoryName || t('checklist.noCategory');

        if (!acc[categoryKey]) {
          acc[categoryKey] = {
            categoryName,
            checklists: [],
          };
        }

        let checklistProgress = 0;
        const related = checklistResponses.filter((r) => r.ChecklistId === checklist.ChecklistId);

        if (related.length > 0) {
          const latest = related.sort(
            (a, b) => b.APChecklistResponseId - a.APChecklistResponseId,
          )[0];
          const questions = checklist.Questions || [];

          if (questions.length > 0) {
            let totalPossibleWeight = 0;
            let achievedWeight = 0;

            questions.forEach((question) => {
              const questionWeight = Number(question.QuestionWeightFloat || 1);
              const optionWeights = (question.Options || []).map((opt) => {
                const ow = opt.OptionWeightFloat;
                return typeof ow === 'number' ? ow : parseFloat(String(ow)) || 0;
              });
              const maxOptionWeight = optionWeights.length ? Math.max(...optionWeights) : 0;
              totalPossibleWeight += questionWeight * maxOptionWeight;
              const answer =
                latest && latest.Answers
                  ? latest.Answers.find(
                      (ans) => ans.ChecklistQuestionId === question.ChecklistQuestionId,
                    )
                  : undefined;

              if (answer) {
                const selectedOption = (question.Options || []).find(
                  (opt) => opt.ChecklistOptionId === answer.ChecklistOptionId,
                );
                let selW = selectedOption?.OptionWeightFloat as number | string | undefined;
                const selNum =
                  typeof selW === 'number' ? selW : parseFloat(String(selW || '0')) || 0;
                achievedWeight += questionWeight * selNum;
              }
            });

            checklistProgress =
              totalPossibleWeight > 0
                ? Math.round(((achievedWeight / totalPossibleWeight) + Number.EPSILON) * 100)
                : 0;
          }
        }

        acc[categoryKey].checklists.push({
          ChecklistId: checklist.ChecklistId,
          Name: checklist.Name,
          progress: checklistProgress,
          CategoryId: checklist.CategoryId ?? null,
          CategoryName: checklist.CategoryName ?? null,
        } as unknown as ProcessedChecklist);

        return acc;
      },
      {} as Record<string, { categoryName: string; checklists: ProcessedChecklist[] }>,
    );

    const categoryStats = Object.entries(categorizedChecklists).reduce(
      (acc, [categoryKey, categoryData]) => {
        const progresses = categoryData.checklists.map((c) => c.progress);

        acc[categoryKey] = {
          ...categoryData,
          minScore: Math.min(...progresses),
          maxScore: Math.max(...progresses),
          avgScore: Math.round(
            (progresses.reduce((sum, p) => sum + p, 0) / progresses.length) + Number.EPSILON,
          ),
        };

        return acc;
      },
      {} as Record<string, CategoryData>,
    );

    return categoryStats;
  }, [checklists, checklistResponses, t]);

  const handleLimpiar = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
    setLoadedSignature(null);
    setSignatureName(null);
    setShowSignatureCanvas(true);
  };

  const handleExit = () => {
    navigate('/');
  };

  const handleGuardarAvance = async () => {
    if (!currentFulfillmentVersion) {
      setAlertMessage(t('ceoValidation.noFulfillmentVersion'));
      return;
    }

    try {
      if (Object.keys(decisionByFocus).length > 0) {
        await Promise.all(
          Object.entries(decisionByFocus).map(([focusId, decision]) =>
            addFocusFulfillmentValidation(currentFulfillmentVersion, {
              APFocusId: parseInt(focusId, 10),
              Approved: decision === 'approve',
            }),
          ),
        );
      }

      if (decisionStatus) {
        const payload: ExtendedPlanValidationInput = {
          Approved: decisionStatus === 'approve',
          CEOConfirmation: validationChecked,
          Comment: comments,
          Signature: signatureName ?? '',
          IsDraft: true,
        };
        await submitPlanValidation(currentFulfillmentVersion, payload);
      } else {
        const payloadDraft: ExtendedPlanValidationInput = {
          Approved: false,
          CEOConfirmation: validationChecked,
          Comment: comments,
          Signature: signatureName ?? '',
          IsDraft: true,
        };
        await submitPlanValidation(currentFulfillmentVersion, payloadDraft);
      }

      setAlertMessage(t('ceoValidation.progressSaved'));
      navigate('/');
    } catch (e) {
      console.error(e);
      setAlertMessage(t('ceoValidation.progressSaveError'));
    }
  };

  const handleGuardarFinalizar = async () => {
    if (readOnlyMode) {
      navigate(showResultsStep ? '/self-assessment-results' : '/');
      return;
    }
    if (!decisionStatus) {
      setAlertMessage(t('approvalDefinitions.mustDecide'));
      return;
    }

    if (decisionStatus === 'approve' && !validationChecked) {
      setAlertMessage(t('ceoValidation.mustConfirmValidation'));
      return;
    }

    if (!currentFulfillmentVersion) {
      setAlertMessage(t('ceoValidation.noFulfillmentVersion'));
      return;
    }

    try {
      await Promise.all(
        Object.entries(decisionByFocus).map(([focusId, decision]) =>
          addFocusFulfillmentValidation(currentFulfillmentVersion, {
            APFocusId: parseInt(focusId, 10),
            Approved: decision === 'approve',
          }),
        ),
      );

      const finalPayload: ExtendedPlanValidationInput = {
        Approved: decisionStatus === 'approve',
        CEOConfirmation: validationChecked,
        Comment: comments,
        Signature: signatureName || '',
        IsDraft: false,
      };
      await submitPlanValidation(currentFulfillmentVersion, finalPayload);
      navigate('/');
    } catch (e) {
      console.error(e);
    }
  };

  const handleOpenModal = (focus: Focus, index: number) => {
    if (!currentFulfillmentVersion) return;
    setSelectedFocus(focus);
    const focusObjectives = objectivesByFocus[focus.APFocusId] || [];
    const firstObjectiveId = focusObjectives[0]?.APObjectiveId || null;
    setSelectedObjective(firstObjectiveId);
    setSelectedIndex(index);
    if (firstObjectiveId) getActivitiesByObjectiveId(firstObjectiveId, currentFulfillmentVersion);
    setAlertMessage(t('ceoValidation.readOnlyMessage'));
    setOpenModal(true);
    setCurrentPage(1);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedFocus(null);
    setSelectedObjective(null);
    setSelectedIndex(null);
    setCurrentPage(1);
    setAlertMessage(null);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const currentObjective =
    selectedFocus && selectedObjective
      ? (objectivesByFocus[selectedFocus.APFocusId] || []).find(
          (o) => o.APObjectiveId === selectedObjective,
        )
      : undefined;

  const rawActivities = currentObjective
    ? activitiesByObjective[currentObjective.APObjectiveId] || []
    : [];

  const getRequirementName = (activity: Activity): string => {
    if (!activity.ApplyRequirement || !activity.APRequirementId) return 'No aplica';
    if (!requirements || !selectedFocus) return '-----';
    const focusReq = requirements.find((r) => r.APFocusId === selectedFocus.APFocusId);
    if (!focusReq) return '-----';
    for (const rt of focusReq.RequirementTypes) {
      const req = rt.requirements.find((r) => r.APRequirementId === activity.APRequirementId);
      if (req) return req.Name;
    }
    return '-----';
  };

  const displayActivities = rawActivities.map((a: Activity) => ({
    id: a.APGroupActivityId,
    name: a.GroupActivityName,
    startDate: a.StartDate,
    endDate: a.EndDate,
    type: a.GroupActivityType,
    requirement: getRequirementName(a),
    adherencePct: a.CompletionProgress,
  }));

  const totalPages = Math.ceil(displayActivities.length / 5) || 1;
  const paginatedActivities = displayActivities.slice((currentPage - 1) * 5, currentPage * 5);

  const nextButtonLabel = React.useMemo(() => {
    if (readOnlyMode && showResultsStep) return t('selfAssessmentResults.viewFinalResults');
    if (readOnlyMode) return t('approvalDefinitions.finish');
    return t('ceoValidation.saveAndFinalize');
  }, [readOnlyMode, showResultsStep, t]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#eef6f6', pt: { xs: 2, md: 4 } }}>
      {/* Content */}
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '1366px', mx: 'auto', width: '100%' }}>
        {/* Main Card */}
        <Paper sx={{ borderRadius: 1, boxShadow: 2, overflow: 'hidden' }}>
          {/* Header */}
          <Grid
            container
            justifyContent={{ xs: 'flex-start', md: 'space-between' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            sx={{
              px: { xs: 2, md: 3 },
              pt: 2,
              bgcolor: '#fafafa',
              borderBottom: '1px solid #A5B2BB',
              rowGap: { xs: 2, md: 0 },
            }}
          >
            <Grid size={{ xs: 12, md: 'auto' }}>
              <Grid
                container
                spacing={2}
                sx={{
                  mb: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                  rowGap: { xs: 1.5, sm: 0 },
                }}
              >
                {/* Annual Plan block */}
                <Grid
                  size={{ xs: 12, sm: 'auto' }}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    backgroundColor: '#FFFFFF',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('common.annualPlan')}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {selectedYear}
                  </Typography>
                </Grid>

                {/* Bloque Ámbito */}
                <Grid
                  size={{ xs: 12, sm: 'auto' }}
                  sx={{
                    px: 2,
                    py: 1,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('newAnnualPlan.scope')} {scopeName}
                    {currentBusinessUnit
                      ? ` · ${t('newAnnualPlan.businessUnit')}: ${currentBusinessUnit}`
                      : ''}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {t('common.self_assessment')}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              size={{ xs: 12, md: 'auto' }}
              sx={{
                display: 'flex',
                justifyContent: { xs: 'flex-start', md: 'center' },
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={PlanAnualIcon}
                  alt={t('common.annualPlan')}
                  style={{ width: 70, height: 70 }}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Stepper - usando el mismo estilo que DefinirActividades */}
          <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'white' }}>
            <Grid container spacing={2} sx={{ mb: 4, flexWrap: 'wrap' }}>
              <Grid size={stepGridSize}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    bgcolor: '#D8FFEC',
                    color: '#333',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #A5B2BB',
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size="auto">
                      <Grid
                        container
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          border: '1px solid #A5B2BB',
                          bgcolor: '#4caf50',
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid size="auto" sx={{ mt: 0.5 }}>
                          <Check />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid size="auto">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('common.step1')}
                      </Typography>
                      <Typography variant="caption">{t('ceoValidation.step1Caption')}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid size={stepGridSize}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    bgcolor: '#D8FFEC',
                    color: '#333',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #A5B2BB',
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size="auto">
                      <Grid
                        container
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          border: '1px solid #A5B2BB',
                          bgcolor: '#4caf50',
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid size="auto" sx={{ mt: 0.5 }}>
                          <Check />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid size="auto">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('common.step2')}
                      </Typography>
                      <Typography variant="caption">{t('ceoValidation.step2Caption')}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid size={stepGridSize}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    bgcolor: '#00BCE4',
                    color: 'white',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #A5B2BB',
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size="auto">
                      <Grid
                        container
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: 'white',
                          color: '#00BCE4',
                          fontWeight: 'bold',
                        }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid size="auto">3</Grid>
                      </Grid>
                    </Grid>
                    <Grid size="auto">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('common.step3')}
                      </Typography>
                      <Typography variant="caption">{t('ceoValidation.step3Caption')}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              {showResultsStep && (
                <Grid size={stepGridSize}>
                  <Paper
                    elevation={0}
                    onClick={handleGoToResults}
                    sx={{
                      flex: 1,
                      bgcolor: '#f5f5f5',
                      color: '#999',
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid #A5B2BB',
                      height: '100%',
                      cursor: 'pointer',
                    }}
                  >
                    <Grid container alignItems="center" spacing={2}>
                      <Grid size="auto">
                        <Grid
                          container
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            border: '1px solid #ccc',
                            color: '#999',
                            fontWeight: 'bold',
                          }}
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Grid size="auto">4</Grid>
                        </Grid>
                      </Grid>
                      <Grid size="auto">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {t('common.step')} 4
                        </Typography>
                        <Typography variant="caption">{t('ceoValidation.step4Caption')}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </Grid>

            {/* ponderado */}
            <Grid
              container
              spacing={2}
              sx={{ mb: 4, border: '1px solid #A5B2BB', borderRadius: 1 }}
            >
              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  p: 2,
                }}
              >
                <Grid
                  container
                  alignItems="center"
                  justifyContent={{ xs: 'center', md: 'space-between' }}
                  spacing={2}
                  size={12}
                >
                  <Grid
                    size={{ xs: 12, md: 6 }}
                    sx={{
                      py: 1,
                      textAlign: { xs: 'center', md: 'left' },
                    }}
                  >
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {t('ceoValidation.weightedCompliance')}
                    </Typography>
                  </Grid>
                  <Grid
                    container
                    justifyContent="center"
                    size={{ xs: 12, md: 6 }}
                    sx={{
                      backgroundColor: '#04B763',
                      borderRadius: 1,
                      p: 2,
                      width: { xs: '100%', md: '20rem' },
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                      }}
                    >
                      {formatPercentage(globalAdherence, 1)}%
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Compliance by Strategic Focuses - updated per mockup */}
            <Accordion
              expanded={expandedAccordion}
              onChange={() => setExpandedAccordion(!expandedAccordion)}
              sx={{
                mb: 2,
                boxShadow: 'none',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  borderRadius: 1,
                  border: '1px solid #A5B2BB',
                  minHeight: '40px !important',
                  height: '40px !important',
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    margin: '8px 0 !important',
                  },
                  '&.Mui-expanded': {
                    minHeight: '40px !important',
                    height: '40px !important',
                  },
                  '& .MuiAccordionSummary-content.Mui-expanded': {
                    margin: '8px 0 !important',
                  },
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {t('annualPlanning.strategicFocuses')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  bgcolor: 'white',
                  p: 0,
                  borderRadius: 1,
                  py: 1,
                  border: '1px solid #A5B2BB',
                }}
              >
                <Box sx={{ p: 1 }}>
                  {focuses.map((foco, index) => (
                    <Grid
                      container
                      spacing={2}
                      key={foco.APFocusId}
                      sx={{
                        mb: 2,
                        mx: 1,
                        display: 'flex',
                        alignItems: 'stretch',
                      }}
                    >
                      {/* Paper principal del foco */}
                      <Grid
                        size={{ xs: 12, lg: 9 }}
                        sx={{
                          display: 'flex',
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            border: '1px solid #A5B2BB',
                            borderRadius: 1,
                            bgcolor: '#F4F8F8',
                            width: '100%',
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { xs: 'flex-start', md: 'center' },
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: { xs: 'flex-start', md: 'center' },
                              flexDirection: { xs: 'column', md: 'row' },
                              width: '100%',
                              gap: { xs: 2, md: 0 },
                            }}
                          >
                            {/* Contenido principal - ocupa el espacio disponible */}
                            <Box sx={{ flex: 1, minWidth: 0, pr: { xs: 0, md: 2 } }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {t('ceoValidation.focusCompliance')}
                              </Typography>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 600,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                }}
                              >
                                {index + 1}. {foco.Name}
                              </Typography>
                              <Grid container spacing={1} sx={{ mt: 1 }}>
                                <Grid size={{ xs: 12, sm: 'auto' }}>
                                  {(() => {
                                    const formatDate = (d?: string | null) => {
                                      if (!d) return null;
                                      const date = new Date(d);
                                      return date.toLocaleDateString(undefined, {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: '2-digit',
                                      });
                                    };
                                    const startDate = formatDate(foco.StartDate);
                                    const endDate = formatDate(foco.EndDate);
                                    const rangeText =
                                      startDate && endDate
                                        ? `${startDate} ${t('approvalDefinitions.dateRangeSeparator')} ${endDate}`
                                        : t('ceoValidation.noActivitiesDefined');
                                    return (
                                      <Box
                                        sx={{
                                          mt: 1,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                          px: 1.5,
                                          py: 0.75,
                                          bgcolor: '#FFFFFF',
                                          border: '1px solid #A5B2BB',
                                          borderRadius: 1,
                                          width: { xs: '100%', sm: 'auto' },
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: 24,
                                            height: 24,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                          }}
                                        >
                                          <img
                                            src={CalendarIcon}
                                            alt={t('common.calendar')}
                                            style={{ width: 20, height: 20 }}
                                          />
                                        </Box>
                                        <Typography
                                          variant="caption"
                                          sx={{ color: '#333', fontWeight: 600, lineHeight: 1.2 }}
                                        >
                                          {t('ceoValidation.period')} {rangeText}
                                        </Typography>
                                      </Box>
                                    );
                                  })()}
                                </Grid>
                                <Grid size={{ xs: 12, sm: 'auto' }}>
                                  {(() => {
                                    const pctRaw = foco.FulfillmentProgressPct ?? 0;
                                    const hasValue = typeof pctRaw === 'number';
                                    const pct = hasValue ? pctRaw : 0;
                                    const pctText = hasValue
                                      ? `${formatPercentage(pct, 1)}%`
                                      : 'N/A';
                                    return (
                                      <Box
                                        sx={{
                                          mt: 1,
                                          bgcolor: '#CEFAFF',
                                          borderRadius: 1,
                                          px: 1.5,
                                          py: 0.9,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1.5,
                                          width: { xs: '100%', sm: 240 },
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontWeight: 600,
                                            color: '#333',
                                            lineHeight: 1,
                                            flex: 1,
                                          }}
                                        >
                                          {t('ceoValidation.focusFulfillmentPct')}
                                        </Typography>
                                        <Typography
                                          variant="h6"
                                          sx={{
                                            fontWeight: 700,
                                            color: hasValue ? '#000' : '#777',
                                            width: '100%',
                                            textAlign: 'center',
                                          }}
                                        >
                                          {pctText}
                                        </Typography>
                                      </Box>
                                    );
                                  })()}
                                </Grid>
                              </Grid>
                            </Box>

                            {/* Periodo - ancho fijo */}
                            <Box
                              sx={{
                                width: { xs: '100%', md: 180 },
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                mt: { xs: 2, md: 0 },
                              }}
                            >
                              <Button
                                variant="outlined"
                                startIcon={<Visibility />}
                                disabled={isAnyLoading}
                                onClick={() => handleOpenModal(foco, index)}
                                size="small"
                                sx={{
                                  borderColor: '#3A4048',
                                  color: '#3A4048',
                                  px: { xs: 2.5, md: 4 },
                                  py: 1,
                                  fontWeight: 600,
                                  borderRadius: 20,
                                  textTransform: 'none',
                                  width: { xs: '100%', md: 'auto' },
                                  '&:hover': {
                                    borderColor: '#212529ff',
                                    bgcolor: 'rgba(3, 22, 24, 0.04)',
                                  },
                                }}
                              >
                                {t('approvalDefinitions.viewDetail')}
                              </Button>
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>

                      {/* Paper de aprobación */}
                      <Grid
                        size={{ xs: 12, lg: 3 }}
                        sx={{
                          display: 'flex',
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            border: '1px solid #A5B2BB',
                            borderRadius: 1,
                            bgcolor: '#F4F8F8',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Grid
                            container
                            alignItems="center"
                            justifyContent="center"
                            spacing={1.5}
                            sx={{ flexWrap: 'wrap' }}
                          >
                            <Grid size={{ xs: 12, sm: 'auto' }} sx={{ mr: { xs: 0, sm: 2 } }}>
                              <Typography variant="subtitle2">
                                {t('ceoValidation.approveDefinitionsQuestion')}
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 'auto' }}>
                              <RadioGroup
                                value={decisionByFocus[foco.APFocusId] || 'reject'}
                                key={foco.APFocusId}
                                onChange={(e) => {
                                  const value = e.target.value as 'approve' | 'reject';
                                  setDecisionByFocus((prev) => ({
                                    ...prev,
                                    [foco.APFocusId]: value,
                                  }));
                                }}
                                sx={{
                                  display: 'flex',
                                  flexDirection: { xs: 'column', sm: 'row' },
                                  gap: 1,
                                  px: 2,
                                  alignItems: { xs: 'stretch', sm: 'center' },
                                }}
                              >
                                <FormControlLabel
                                  value="approve"
                                  disabled={!!readOnlyMode}
                                  control={<Radio size="small" />}
                                  label={t('common.approve')}
                                />
                                <FormControlLabel
                                  value="reject"
                                  disabled={!!readOnlyMode}
                                  control={<Radio size="small" />}
                                  label={t('common.reject')}
                                />
                              </RadioGroup>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                    </Grid>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Nueva sección de Gestión de Seguridad - Checklists */}
            {Object.keys(processedChecklistData).length > 0 && (
              <Accordion
                expanded={true}
                sx={{
                  mt: 3,
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    bgcolor: '#F4F8F8',
                    borderRadius: 1,
                    border: '1px solid #A5B2BB',
                    minHeight: 'auto',
                    height: 'auto',
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      margin: '8px 0 !important',
                    },
                    '&.Mui-expanded': {
                      minHeight: 'auto',
                      height: 'auto',
                    },
                    '& .MuiAccordionSummary-content.Mui-expanded': {
                      margin: '8px 0 !important',
                    },
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {scopeName} - {t('ceoValidation.securityManagementSelfAssessment')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    bgcolor: 'white',
                    p: 1,
                    mt: 1,
                    borderRadius: 1,
                    border: '1px solid #A5B2BB',
                  }}
                >
                  <Box sx={{ p: 1 }}>
                    {Object.entries(processedChecklistData).map(([categoryKey, categoryData]) => (
                      <Accordion
                        key={categoryKey}
                        expanded={expandedChecklistCategories[categoryKey] ?? true}
                        onChange={() =>
                          setExpandedChecklistCategories((prev) => ({
                            ...prev,
                            [categoryKey]: !prev[categoryKey],
                          }))
                        }
                        sx={{
                          mb: 2,
                          boxShadow: 'none',
                          '&:before': { display: 'none' },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMore />}
                          sx={{
                            bgcolor: '#F4F8F8',
                            borderRadius: 1,
                            border: '1px solid #A5B2BB',
                            minHeight: 'auto',
                            height: 'auto',
                            '& .MuiAccordionSummary-content': {
                              alignItems: 'center',
                              margin: '8px 0 !important',
                            },
                            '&.Mui-expanded': {
                              minHeight: 'auto',
                              height: 'auto',
                            },
                            '& .MuiAccordionSummary-content.Mui-expanded': {
                              margin: '8px 0 !important',
                            },
                          }}
                        >
                          <Grid
                            container
                            alignItems="center"
                            justifyContent={{ xs: 'flex-start', md: 'space-between' }}
                            spacing={1.5}
                            sx={{ width: '100%', pr: { xs: 0, md: 2 }, flexWrap: 'wrap' }}
                          >
                            <Grid size={{ xs: 12, md: 'auto' }}>
                              <Grid container alignItems="center" spacing={2}>
                                <Grid size="auto">
                                  <CheckCircle
                                    sx={{
                                      color: categoryData.avgScore > 0 ? '#4caf50' : '#A5B2BB',
                                    }}
                                  />
                                </Grid>
                                <Grid size="auto">
                                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {categoryData.categoryName}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#666' }}>
                                    {categoryData.checklists.length} checklist
                                    {categoryData.checklists.length !== 1 ? 's' : ''}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid
                              size={{ xs: 12, md: 'auto' }}
                              sx={{
                                display: 'flex',
                                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                width: '100%',
                              }}
                            >
                              <Grid
                                container
                                alignItems="center"
                                spacing={2}
                                sx={{
                                  flexWrap: 'wrap',
                                  justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                }}
                              >
                                <Grid size={{ xs: 12, sm: 'auto' }}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 2,
                                      bgcolor: 'white',
                                      border: '1px solid #E0E0E0',
                                      borderRadius: 2,
                                      px: 2,
                                      py: 1,
                                      width: { xs: '100%', md: 'auto' },
                                      justifyContent: { xs: 'flex-start', sm: 'flex-start' },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        border: '1px solid #E0E0E0',
                                        borderRadius: 2,
                                        px: 1,
                                        py: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        width: { xs: '100%', sm: 'auto' },
                                        justifyContent: { xs: 'flex-start', sm: 'flex-start' },
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 500, color: '#666' }}
                                      >
                                        {t('common.min')}: {categoryData.minScore}%
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        border: '1px solid #E0E0E0',
                                        borderRadius: 2,
                                        px: 1,
                                        py: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        width: { xs: '100%', sm: 'auto' },
                                        justifyContent: { xs: 'flex-start', sm: 'flex-start' },
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 500, color: '#666' }}
                                      >
                                        {t('common.max')}: {categoryData.maxScore}%
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        border: '1px solid #E0E0E0',
                                        borderRadius: 2,
                                        px: 1,
                                        py: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        bgcolor: '#CEFAFF',
                                        width: { xs: '100%', sm: 'auto' },
                                        justifyContent: { xs: 'flex-start', sm: 'flex-start' },
                                      }}
                                    >
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {t('common.average')}: {categoryData.avgScore}%
                                      </Typography>
                                      <LightTooltip
                                        title={t('ceoValidation.preliminaryResultsTooltip')}
                                        arrow
                                      >
                                        <InfoOutlined
                                          sx={{ fontSize: 20, color: '#607d8b', flexShrink: 0 }}
                                        />
                                      </LightTooltip>
                                    </Box>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </AccordionSummary>
                        <AccordionDetails
                          sx={{
                            bgcolor: 'white',
                            p: 1,
                            mt: 1,
                            borderRadius: 1,
                            border: '1px solid #A5B2BB',
                          }}
                        >
                          <Box sx={{ p: 1 }}>
                            {categoryData.checklists.map((checklist) => (
                              <Paper
                                key={checklist.ChecklistId}
                                elevation={0}
                                sx={{
                                  mb: 1,
                                  p: 2,
                                  border: '1px solid #A5B2BB',
                                  borderRadius: 1,
                                  bgcolor: '#F4F8F8',
                                }}
                              >
                                <Grid
                                  container
                                  alignItems="center"
                                  justifyContent={{ xs: 'flex-start', md: 'space-between' }}
                                  spacing={1.5}
                                  sx={{ width: '100%', flexWrap: 'wrap' }}
                                >
                                  <Grid size={{ xs: 12, md: 'auto' }}>
                                    <Grid container alignItems="center" spacing={2}>
                                      <Grid size="auto">
                                        <CheckCircleOutline
                                          sx={{
                                            color: checklist.progress > 0 ? '#4caf50' : '#A5B2BB',
                                            fontSize: 20,
                                          }}
                                        />
                                      </Grid>
                                      <Grid size="auto">
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {checklist.Name}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                  <Grid
                                    size={{ xs: 12, md: 'auto' }}
                                    sx={{
                                      display: 'flex',
                                      justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                      width: '100%',
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        bgcolor: '#CEFAFF',
                                        borderRadius: 2,
                                        px: 2,
                                        py: 1,
                                        border: '1px solid #E0E0E0',
                                        width: { xs: '100%', md: 'auto' },
                                        justifyContent: { xs: 'flex-start', md: 'center' },
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 600,
                                          color: '#333',
                                        }}
                                      >
                                        {formatPercentage(checklist.progress, 1)}%
                                      </Typography>
                                      <LightTooltip
                                        title={t('ceoValidation.preliminaryResultsTooltip')}
                                        arrow
                                      >
                                        <InfoOutlined
                                          sx={{ fontSize: 20, color: '#607d8b', flexShrink: 0 }}
                                        />
                                      </LightTooltip>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Paper>
                            ))}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {/* New section: Final Annual Plan Decision */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 1,
                overflow: 'hidden',
                mt: 3,

                border: '1px solid #A5B2BB',
              }}
            >
              {/* Header con fondo cyan claro */}
              <Box
                sx={{
                  mb: 1,
                  bgcolor: '#CEFAFF',
                  borderRadius: 1,
                  m: 2,
                  p: 2.5,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('ceoValidation.finalDecisionTitle')}
                </Typography>

                {/* Validación por parte del CEO text and toggle */}
                <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
                  <Grid size={{ xs: 12, md: 'auto' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
                      {t('ceoValidation.ceoValidationTitle')}
                    </Typography>
                    <Typography variant="body2">
                      {t('ceoValidation.decisionQuestion', { year: selectedYear ?? '' })}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 'auto' }}>
                    <ToggleButtonGroup
                      value={decisionStatus}
                      disabled={!!readOnlyMode}
                      exclusive
                      onChange={(_, newValue) => newValue && setDecisionStatus(newValue)}
                      sx={{
                        bgcolor: '#A2F2FF',
                        border: '1px solid #00BCE4',
                        borderRadius: 20,
                        p: 0.5,
                        width: '100%',
                        flexWrap: { xs: 'wrap', sm: 'nowrap' },
                        justifyContent: { xs: 'center', md: 'flex-start' },
                        display: 'flex',
                        '& .MuiToggleButton-root': {
                          flex: 1,
                          flexWrap: 'wrap',
                        },
                      }}
                    >
                      <ToggleButton
                        value="approve"
                        disabled={!allFocusApproved || !!readOnlyMode}
                        sx={{
                          px: 4,
                          py: 1,
                          borderRadius: 20,
                          textTransform: 'none',
                          border: 'none',
                          fontWeight: 500,
                          flexGrow: { xs: 1, sm: 0 },
                          justifyContent: 'center',
                          '&.Mui-selected': {
                            bgcolor: '#3A4048',
                            color: 'white',
                            '&:hover': {
                              bgcolor: '#424242',
                            },
                          },
                          '&:not(.Mui-selected)': {
                            bgcolor: 'transparent',
                            '&:hover': {
                              bgcolor: 'rgba(0, 105, 92, 0.08)',
                            },
                          },
                        }}
                      >
                        <Check sx={{ mr: 1, fontSize: 20 }} />
                        {t('common.approve')}
                      </ToggleButton>
                      <ToggleButton
                        value="reject"
                        sx={{
                          px: 4,
                          py: 1,
                          borderRadius: 20,
                          textTransform: 'none',
                          border: 'none',
                          fontWeight: 500,
                          flexGrow: { xs: 1, sm: 0 },
                          justifyContent: 'center',
                          '&.Mui-selected': {
                            bgcolor: '#3A4048',
                            color: 'white',
                            '&:hover': {
                              bgcolor: '#424242',
                            },
                          },
                          '&:not(.Mui-selected)': {
                            bgcolor: 'transparent',
                            '&:hover': {
                              bgcolor: 'rgba(0, 105, 92, 0.08)',
                            },
                          },
                        }}
                      >
                        <Close sx={{ mr: 1, fontSize: 20 }} />
                        {t('common.reject')}
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                </Grid>
              </Box>
              <Box>
                {/* Conditional form based on decision */}
                {decisionStatus && (
                  <Box sx={{ mt: 3 }}>
                    {/* For Approve */}
                    {decisionStatus === 'approve' && (
                      <Paper
                        elevation={0}
                        sx={{ mb: 2, mx: 2, p: 2, bgcolor: '#F4F8F8', border: '1px solid #A5B2BB' }}
                      >
                        {/* Validation checkbox */}
                        <Grid container spacing={3}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                              {t('ceoValidation.ceoAnnualPlanValidation')}
                            </Typography>
                            <Paper
                              elevation={0}
                              sx={{ p: 2, bgcolor: '#fafafa', border: '1px solid #A5B2BB' }}
                            >
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    disabled={!!readOnlyMode}
                                    checked={validationChecked}
                                    onChange={(e) => setValidationChecked(e.target.checked)}
                                    sx={{
                                      height: 40,
                                      width: 60,
                                    }}
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                      {t('ceoValidation.ceoAnnualPlanValidationShort', {
                                        year: selectedYear ?? '',
                                      })}
                                    </Typography>
                                    <Typography variant="subtitle2">
                                      {t('ceoValidation.declareApprovedNoMoreInfo')}
                                    </Typography>
                                  </Box>
                                }
                                sx={{ alignItems: 'flex-start' }}
                              />
                            </Paper>
                          </Grid>

                          {/* Comments */}
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                              {t('ceoValidation.addComments')}
                            </Typography>

                            <TextField
                              fullWidth
                              required
                              multiline
                              rows={3}
                              disabled={!!readOnlyMode}
                              placeholder={t('defineActivities.enterText')}
                              value={comments}
                              onChange={(e) => setComments(e.target.value)}
                              variant="outlined"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  bgcolor: 'white',
                                },
                              }}
                            />
                          </Grid>

                          {/* Digital Signature - for both Approve and Reject */}
                          <Grid size={12}>
                            <Grid container justifyContent="center" spacing={2}>
                              <Grid size={12}>
                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                  {t('ceoValidation.addDigitalSignature')}
                                </Typography>

                                <Box
                                  sx={{
                                    borderRadius: 1,
                                    bgcolor: 'white',
                                    border: '1px solid #A5B2BB',
                                    mb: 2,
                                    p: 2,
                                    position: 'relative',
                                  }}
                                >
                                  {showSignatureCanvas ? (
                                    <>
                                      <SigCanvas
                                        ref={(inst: SignaturePadHandle | null) => {
                                          signatureRef.current = inst;
                                        }}
                                        penColor={!!readOnlyMode ? 'white' : 'black'}
                                        canvasProps={{
                                          width: 1100,
                                          height: 220,
                                          style: { border: 'none' },
                                        }}
                                      />
                                    </>
                                  ) : loadedSignature ? (
                                    <Box>
                                      <Box sx={{ position: 'relative', minHeight: 200 }}>
                                        <img
                                          src={loadedSignature}
                                          alt="signature"
                                          style={{ maxHeight: '180px', maxWidth: '100%' }}
                                        />
                                      </Box>
                                    </Box>
                                  ) : null}
                                </Box>
                              </Grid>
                              <Grid size={12}>
                                <Grid container justifyContent="right" spacing={2} sx={{ mt: 2 }}>
                                  <Grid size="auto">
                                    <Button
                                      variant="outlined"
                                      onClick={handleLimpiar}
                                      disabled={isAnyLoading || !!readOnlyMode}
                                      sx={{
                                        borderColor: '#00BCE4',
                                        color: '#00BCE4',
                                        px: 4,
                                        py: 1,
                                        borderRadius: 20,
                                        textTransform: 'none',
                                        '&:hover': {
                                          borderColor: '#00acc1',
                                          bgcolor: 'rgba(0, 188, 212, 0.04)',
                                        },
                                      }}
                                    >
                                      {t('common.clear')}
                                    </Button>
                                  </Grid>
                                  <Grid size="auto">
                                    <Button
                                      variant="contained"
                                      onClick={handleUploadSignature}
                                      disabled={isAnyLoading || !!readOnlyMode}
                                      sx={{
                                        bgcolor: '#00BCE4',
                                        px: 4,
                                        py: 1,
                                        borderRadius: 20,
                                        textTransform: 'none',
                                        '&:hover': { bgcolor: '#00acc1' },
                                      }}
                                    >
                                      {t('common.save')}
                                    </Button>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Paper>
                    )}

                    {/* For Reject */}
                    {decisionStatus === 'reject' && (
                      <>
                        {/* Comments */}
                        <Grid size={12}>
                          <Paper
                            elevation={0}
                            sx={{
                              mb: 2,
                              mx: 2,
                              p: 2,
                              bgcolor: '#F4F8F8',
                              border: '1px solid #A5B2BB',
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                              {t('ceoValidation.addComments')}
                            </Typography>
                            <TextField
                              fullWidth
                              required
                              multiline
                              rows={4}
                              disabled={!!readOnlyMode}
                              placeholder={t('defineActivities.enterText')}
                              value={comments}
                              onChange={(e) => setComments(e.target.value)}
                              variant="outlined"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  bgcolor: 'white',
                                },
                              }}
                            />
                            <Grid container justifyContent="center" spacing={2} sx={{ mt: 2 }}>
                              <Grid size={12}>
                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                  {t('ceoValidation.addDigitalSignature')}
                                </Typography>

                                <Box
                                  sx={{
                                    borderRadius: 1,
                                    bgcolor: 'white',
                                    border: '1px solid #A5B2BB',
                                    mb: 2,
                                    p: 2,
                                    position: 'relative',
                                  }}
                                >
                                  {showSignatureCanvas ? (
                                    <>
                                      <SigCanvas
                                        ref={(inst: SignaturePadHandle | null) => {
                                          signatureRef.current = inst;
                                        }}
                                        penColor={!!readOnlyMode ? 'white' : 'black'}
                                        canvasProps={{
                                          width: 1100,
                                          height: 220,
                                          style: { border: 'none' },
                                        }}
                                      />
                                    </>
                                  ) : loadedSignature ? (
                                    <Box>
                                      <Box sx={{ position: 'relative', minHeight: 200 }}>
                                        <img
                                          src={loadedSignature}
                                          alt="signature"
                                          style={{ maxHeight: '180px', maxWidth: '100%' }}
                                        />
                                      </Box>
                                    </Box>
                                  ) : null}
                                </Box>
                              </Grid>
                              <Grid size={12}>
                                <Grid container justifyContent="right" spacing={2} sx={{ mt: 2 }}>
                                  <Grid size="auto">
                                    <Button
                                      variant="outlined"
                                      onClick={handleLimpiar}
                                      disabled={isAnyLoading || !!readOnlyMode}
                                      sx={{
                                        borderColor: '#00BCE4',
                                        color: '#00BCE4',
                                        px: 4,
                                        py: 1,
                                        borderRadius: 20,
                                        textTransform: 'none',
                                        '&:hover': {
                                          borderColor: '#00acc1',
                                          bgcolor: 'rgba(0, 188, 212, 0.04)',
                                        },
                                      }}
                                    >
                                      {t('common.clear')}
                                    </Button>
                                  </Grid>
                                  <Grid size="auto">
                                    <Button
                                      variant="contained"
                                      onClick={handleUploadSignature}
                                      disabled={isAnyLoading || !!readOnlyMode}
                                      sx={{
                                        bgcolor: '#00BCE4',
                                        px: 4,
                                        py: 1,
                                        borderRadius: 20,
                                        textTransform: 'none',
                                        '&:hover': { bgcolor: '#00acc1' },
                                      }}
                                    >
                                      {t('common.save')}
                                    </Button>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Botones de navegación */}
            <Navigation
              onNext={handleGuardarFinalizar}
              nextTitle={nextButtonLabel}
              disableNext={disableNext}
              disableReason={disableNextReason}
              onSaveDraft={handleGuardarAvance}
              saveDraftTitle={t('ceoValidation.saveDraft')}
              onExit={handleExit}
              exitTitle={t('common.exit')}
              isAnyLoading={isAnyLoading}
              disableSaveDraft={false}
              readOnlyMode={readOnlyMode}
            />
          </Box>
        </Paper>
      </Box>

      {/* Modal de Detalle */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, bgcolor: '#F4F8F8' }}>
          <Typography variant="subtitle1" component="div" lineHeight={1.5}>
            {t('ceoValidation.focusModalTitle')}
          </Typography>
          <Typography variant="subtitle1" component="div" lineHeight={1} sx={{ fontWeight: 600 }}>
            {selectedIndex ? selectedIndex + 1 : 1}. {selectedFocus?.Name}
          </Typography>
          <IconButton
            aria-label="close"
            disabled={isAnyLoading}
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 16,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {/* Alert Message */}
          {alertMessage && (
            <Box
              sx={{
                p: 2,
                mb: 2,
                bgcolor: '#FEFFC2',
                borderLeft: '4px solid #C99D00',
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                width: '100%',
                flexShrink: 0,
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1.5, sm: 0 },
              }}
            >
              <Typography variant="body2">{alertMessage}</Typography>
              <IconButton
                size="small"
                onClick={() => setAlertMessage(null)}
                disabled={isAnyLoading}
                sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          )}
          {selectedFocus && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2, flex: 1, overflow: 'hidden' }}>
                <Grid
                  size={{ xs: 12, lg: 4 }}
                  sx={{
                    overflow: 'hidden',
                    p: 2,
                    border: '1px solid #A5B2BB',
                    height: { xs: 'auto', lg: '100%' },
                    overflowY: 'auto',
                    borderRadius: 1,
                  }}
                >
                  <Accordion
                    expanded={true}
                    sx={{
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      bgcolor: 'white',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{
                        borderRadius: 1,
                        height: '100%',
                        bgcolor: '#e8f9fb',
                        borderLeft: '4px solid #00BCE4',
                        '& .MuiAccordionSummary-content': {
                          my: 1,
                        },
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {t('ceoValidation.objectivesByFocus')}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <List>
                        {(objectivesByFocus[selectedFocus.APFocusId] || []).map((objetivo) => (
                          <ListItem key={objetivo.APObjectiveId} disablePadding>
                            <ListItemButton
                              selected={objetivo.APObjectiveId === selectedObjective}
                              onClick={() => {
                                setSelectedObjective(objetivo.APObjectiveId);
                                if (currentFulfillmentVersion) {
                                  getActivitiesByObjectiveId(
                                    objetivo.APObjectiveId,
                                    currentFulfillmentVersion,
                                  );
                                }
                                setCurrentPage(1);
                              }}
                              sx={{
                                borderRadius: 1,
                                '&.Mui-selected': {
                                  backgroundColor: '#E8F5F5',
                                  '&:hover': {
                                    backgroundColor: '#E8F5F5',
                                  },
                                },
                              }}
                            >
                              <ListItemIcon>
                                <ChevronRight />
                              </ListItemIcon>
                              <Tooltip title={objetivo.Name} placement="right">
                                <ListItemText
                                  primary={objetivo.Name}
                                  primaryTypographyProps={{
                                    variant: 'body2',
                                    style: {
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                    },
                                  }}
                                />
                              </Tooltip>
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                <Grid
                  size={{ xs: 12, lg: 8 }}
                  sx={{
                    overflow: 'hidden',
                    p: 2,
                    border: '1px solid #A5B2BB',
                    height: { xs: 'auto', lg: '100%' },
                    overflowY: 'auto',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                    {t('ceoValidation.objectiveActivities')}
                  </Typography>
                  {currentObjective && (
                    <>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'transparent' }}>
                              {[
                                t('table.activityName'),
                                t('ceoValidation.termRange'),
                                t('table.activityType'),
                                t('table.activityRequirement'),
                                t('ceoValidation.compliancePct'),
                              ].map((label, idx) => (
                                <TableCell
                                  key={label}
                                  align={idx === 5 ? 'center' : 'left'}
                                  sx={{
                                    p: 0.25,
                                    borderBottom: 'none',
                                    bgcolor: 'transparent',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      bgcolor: '#DEEAEB',
                                      border: `1px solid #d5e2e7`,
                                      px: 1,
                                      py: 1,
                                      height: 48,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      borderTopLeftRadius: 8,
                                      borderTopRightRadius: 8,
                                      boxShadow:
                                        'inset 0 1px 0 rgba(255,255,255,0.85), 0 1px 2px rgba(0,0,0,0.06)',
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        fontWeight: 600,
                                        color: '#333',
                                        lineHeight: 1.2,
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {label}
                                    </Typography>
                                  </Box>
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paginatedActivities.map((actividad, index, arr) => {
                              const rowBg = index % 2 === 0 ? '#EFF5F5' : '#FAFCFC';
                              const isLastRow = index === arr.length - 1;
                              const innerCommon = {
                                border: `1px solid #e1eaee`,
                                px: 1,
                                py: 1,
                                bgcolor: rowBg,
                                boxShadow:
                                  'inset 0 1px 0 rgba(255,255,255,0.9), 0 1px 1px rgba(0,0,0,0.04)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 48,
                                ...(isLastRow && {
                                  borderBottomLeftRadius: 8,
                                  borderBottomRightRadius: 8,
                                }),
                              } as const;

                              const requirementName = actividad.requirement || '';

                              return (
                                <TableRow key={actividad.id} sx={{ bgcolor: 'transparent' }}>
                                  <TableCell sx={{ p: 0.25, borderBottom: 'none' }}>
                                    <Box sx={innerCommon}>
                                      <Tooltip title={actividad.name || ''} arrow placement="top">
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontSize: '0.875rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: '200px',
                                            cursor: 'default',
                                          }}
                                        >
                                          {actividad.name}
                                        </Typography>
                                      </Tooltip>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ p: 0.25, borderBottom: 'none' }}>
                                    <Box sx={innerCommon}>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: '0.875rem',
                                        }}
                                      >
                                        {actividad.startDate && actividad.endDate
                                          ? `${new Date(actividad.startDate).toLocaleDateString()} ${t('pdf.separator')} ${new Date(actividad.endDate).toLocaleDateString()}`
                                          : `- ${t('pdf.separator')} -`}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ p: 0.25, borderBottom: 'none' }}>
                                    <Box
                                      sx={{
                                        ...innerCommon,
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          bgcolor: '#F4F8F8',
                                          border: '1px solid #A5B2BB',
                                          px: 1.5,
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 1,
                                          fontSize: '0.8125rem',
                                          minHeight: 30,
                                          fontWeight: 400,
                                          textAlign: 'center',
                                          borderRadius: 1,
                                        }}
                                      >
                                        {actividad.type === ActivityTypes.STRATEGIC
                                          ? t('defineActivities.strategic')
                                          : t('defineActivities.permanent')}
                                      </Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ p: 0.25, borderBottom: 'none' }}>
                                    <Box sx={innerCommon}>
                                      <Tooltip title={requirementName} arrow placement="top">
                                        <Typography
                                          variant="body2"
                                          textAlign={'center'}
                                          sx={{
                                            fontSize: '0.875rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: '200px',
                                            cursor: 'default',
                                          }}
                                        >
                                          {requirementName.length > 120
                                            ? `${requirementName.substring(0, 120)}...`
                                            : requirementName}
                                        </Typography>
                                      </Tooltip>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ p: 0.25, borderBottom: 'none' }}>
                                    <Box sx={innerCommon}>
                                      <Typography
                                        variant="body2"
                                        textAlign={'center'}
                                        sx={{
                                          fontSize: '0.875rem',
                                        }}
                                      >
                                        {actividad.adherencePct} %
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                          <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={handlePageChange}
                            siblingCount={1}
                            boundaryCount={1}
                            shape="rounded"
                          />
                        </Box>
                      )}
                    </>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CEOValidation;
